import React from 'react';
import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { store } from 'store';

import { apiCompanies } from '~constants/api';

import { formatDateString } from '~libs/dayjs';

import { Budget, Section } from '~support-at-home/models';

import { User } from '~main/reducers/login';

import BudgetSection from './BudgetSection';
import BudgetTotalIndicatorPDF from './BudgetTotalIndicator';
import GrandTotal from './GrandTotal';

interface GenerateBudgetPDFProps {
  budget: Budget;
  user?: User;
  hcpData?: any;
}

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 10,
    padding: 8,
    backgroundColor: 'white',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 5,
  },
  companyInfo: {
    flex: 1,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  companyDetails: {
    fontSize: 10,
    marginBottom: 2,
    lineHeight: 1.4,
  },
  logoContainer: {
    alignItems: 'flex-end',
  },
  budgetTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  budgetDetails: {
    fontSize: 10,
    marginBottom: 5,
  },
  budgetInfo: {
    marginTop: 10,
    marginBottom: 3,
  },
  budgetInfoRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    fontSize: 10,
    marginBottom: 8,
  },
  budgetInfoItemStart: {
    alignItems: 'flex-start',
  },
  budgetInfoItemEnd: {
    alignItems: 'flex-end',
  },
  label: {
    fontWeight: 'bold',
    marginRight: 5,
  },
  separator: {
    borderTop: '1px solid #ddd',
  },
  logo: {
    width: 120,
    height: 'auto',
    flexShrink: 0,
  },
  grandTotalWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
});

const getLogoSource = (logo: string) => {
  const token = store.getState().main.login.sessionToken;
  return logo
    ? {
        uri: logo,
        headers: {
          'x-session-token': token,
        },
      }
    : null;
};

export const GenerateBudgetPDF: React.FC<GenerateBudgetPDFProps> = ({ budget, user, hcpData }) => {
  const { id: company_id, name: company_name, abn, address, state, postcode } = user?.company || {};
  const {
    start_date,
    end_date,
    client,
    price_plan_revision,
    sections,
    grand_total,
    total_includes_case_management = false,
    total_allocated,
    available_amount = 0,
    package_fixed,
    status,
  } = budget;

  const buildSections = (sections: Section[]) => {
    return sections.map((section) => (
      <View wrap={false} key={section.id}>
        <BudgetSection section={section} />
      </View>
    ));
  };

  const logoSource = getLogoSource(`${apiCompanies}/${company_id}/logo`);
  const logoContent = logoSource ? <Image src={logoSource} style={styles.logo} /> : undefined;
  const from = formatDateString(start_date);
  const to = formatDateString(end_date);

  return (
    <Document title={`Budget Report - ${client.full_name} (${from} to ${to})`}>
      <Page size="A4" style={styles.page}>
        {/* Header Section */}
        <View style={styles.header}>
          {/* Company Information */}
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>{company_name}</Text>
            <Text style={styles.companyDetails}>ABN: {abn}</Text>
            <Text style={styles.companyDetails}>
              {address}, {state}, {postcode}
            </Text>
          </View>
          {/* Company Logo */}
          <View style={styles.logoContainer}>{logoContent}</View>
        </View>
        <View style={styles.separator} />
        {/* Budget Information Section */}
        <View style={styles.budgetInfo}>
          <View style={styles.budgetInfoRow}>
            <View style={styles.budgetInfoItemStart}>
              <Text>
                <Text style={styles.label}>Client: </Text>
                {client?.full_name}
              </Text>
              <Text>
                <Text style={styles.label}>Price Plan: </Text>
                {price_plan_revision?.price_plan?.name} - (Rev. {price_plan_revision?.sequence || 'N/A'})
              </Text>
              <Text>
                <Text style={styles.label}>Package Type: </Text>
                {package_fixed?.name ?? 'N/A'}
              </Text>
              <Text>
                <Text style={styles.label}>Funding Type: </Text>
                {package_fixed?.funding_type?.name ?? 'N/A'}
              </Text>
            </View>
            <View style={styles.budgetInfoItemEnd}>
              <Text>
                <Text style={styles.label}>Start Date: </Text>
                {from}
              </Text>
              <Text>
                <Text style={styles.label}>End Date: </Text>
                {to}
              </Text>
              <Text>
                <Text style={styles.label}>Status: </Text>
                {status?.name ?? 'N/A'}
              </Text>
            </View>
          </View>
        </View>
        <View>
          <BudgetTotalIndicatorPDF allocated={total_allocated ?? 0} available={available_amount} hcpData={hcpData} />
        </View>
        {/* Budget Sections */}
        {buildSections(sections)}
        {/* Grand Total Section */}
        <View wrap={false} style={styles.grandTotalWrapper}>
          <GrandTotal totals={grand_total} includesCaseManagement={total_includes_case_management} />
        </View>
      </Page>
    </Document>
  );
};
